/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { gql } from '@apollo/client';
import { cond, renderDefaultLoading, SmartComponentProps } from '@exo/frontend-common-utils';
import { useCategoryWithProducts } from '../../hooks/useCategoryWithProducts';
import { processProductList } from '../../common/productListing';
import { Product } from '../../model/types';
import { CategoryRef } from '../../model/category-ref';
import { useSessionContext } from '@exo/frontend-common-session-context';
import { usePriceRange ,useOnSaleBuyPriceRange} from '../../hooks/usePriceRange';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { waitForRequestsToComplete } from '../../../../../../../packages/common/apollo/client/links/activeRequests';
import { generateUrlWithLanguage } from '../../../../../../utils';

export const CategoryListingContainer = ({
  render,
  renderLoading = renderDefaultLoading,
  categoryId,
  currency,
  selectedFacets = [],
  sort,
  storeId,
  useReviews,
  useAvailability
}: Props) => {
  const {
    loading,
    error,
    data,
    onLoadMore
  } = useCategoryWithProducts<ProductResponse>(
    { categoryId, facets: selectedFacets, sort, currency },
    CategoryListingContainer.fragment(useAvailability, useReviews, storeId)
  );
  const session = useSessionContext();
  const navigate = useNavigate();
  const pricingSessionData = session?.pricingData ?? [];
  const productData = useMemo(() => {
    return processProductList(data?.products?.edges!, pricingSessionData, sort)
  }, [data?.products?.edges!, pricingSessionData, sort])

  const listOfPartNumbers =
    productData && productData?.length
      ? productData?.map(eachProductData => {
        return eachProductData?.partnumber;
      })
      : [];

  const {data: dataOnSalePrice,refetch : refetchOnsale } = useOnSaleBuyPriceRange(listOfPartNumbers, 'CAD', { fetchPolicy: 'network-only' },listOfPartNumbers?.length === 0);

  const {
    loading: loadingPrice,
    error: errorPrice,
    data: dataPrice,
    refetch
  } = usePriceRange(
    listOfPartNumbers,
    'CAD',
    { fetchPolicy: 'network-only' },
    listOfPartNumbers?.length === 0
  );

   useEffect(() => {
    if(dataPrice?.price?.entitledPrice) {
      return;
    }
    const timeoutId = setTimeout(() => {
      session?.set({ pricingData: dataPrice?.price?.entitledPrice });
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [categoryId?.id, dataPrice]);

  useEffect(() => {
    if (listOfPartNumbers?.length !== 0){
      refetch();
      refetchOnsale();
    }
    
  }, [session?.targetTimer]);

  // if (loading || loadingPrice) return renderLoading();
  if (!data) return renderLoading();
  
  if (error || errorPrice) {
    navigate(generateUrlWithLanguage('/error',session.language))
    return;
  }
  const hasMore = !!data?.products?.pageInfo?.hasNextPage;
  return render({
    productData,
    onLoadMore,
    hasMore,
    totalResultsCount: data?.products?.pageInfo?.totalResultsCount,
    prices: dataPrice?.price?.entitledPrice,
    onSaleBuyPrice : dataOnSalePrice
  });
};

type ProductResponse = {
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string;
    totalResultsCount: number;
  };
  edges: {
    cursor: string;
    node: Product;
  }[];
};

CategoryListingContainer.fragment = (availability, reviews, storeId) => gql`
  fragment ProductListWithEverything on PrdResultConnection {
    pageInfo {
      hasNextPage
      endCursor
      totalResultsCount
    }
    edges {
      cursor
      node {
        id
        topParentCatName
        partnumber
        slug
        name
        thumbnail
        type
        buyable
        topParentCatName
        attributes {
          id
          value {
            value
          }
        }
        price {
          list {
            value
            currency
          }
          offer {
            value
            currency
          }
        }

        ${cond(
  reviews,
  `
          reviews {
            averageRating
            reviewCount
          }
        `
)}

        ${cond(
  availability,
  `
          availability {
            status
            shipNode {
              id
              name
            }
            distributionGroup {
              id
            }
          }
        `
)}
      }
    }
  }
`;

type Props = SmartComponentProps<{
  categoryId: CategoryRef;
  currency: string;
  useReviews?: boolean;
  useAvailability?: 'online' | 'store' | 'online+store';
  storeId?: string;
  selectedFacets?: string[];
  sort?: string;
  render: (props: CategoryListingContainerRenderProps) => JSX.Element | any;
}>;

export type CategoryListingContainerRenderProps = {
  onLoadMore: () => void;
  hasMore: boolean;
  totalResultsCount?: number;
  productData: ReturnType<typeof processProductList>;
  prices?: any;
  onSaleBuyPrice?: any;
};
